import PropTypes from 'prop-types'
import React, {
    createContext,
    useCallback,
    useContext,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react'
import GlobalStyles from '@mui/material/GlobalStyles'
import { ThemeProvider } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { darkTheme, lightTheme } from '../theme'
import { themeVariableStyles } from './tokens'

// The same key and values public/index.html reads before the first paint.
export const THEME_STORAGE_KEY = 'comms-v2.theme'
export const THEME_PREFERENCES = ['system', 'light', 'dark']
export const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

/**
 * The saved theme preference, or 'system' when none (or an unknown value, or
 * storage that cannot be read) is saved.
 *
 * @returns {'system'|'light'|'dark'}
 */
export const readThemePreference = () => {
    try {
        const saved = window.localStorage.getItem(THEME_STORAGE_KEY)
        return THEME_PREFERENCES.includes(saved) ? saved : 'system'
    } catch (e) {
        return 'system'
    }
}

/**
 * The theme a preference shows: 'system' follows the device setting.
 *
 * @param {'system'|'light'|'dark'} preference
 * @param {boolean} systemDark the device asks for dark
 * @returns {'light'|'dark'}
 */
export const resolveThemeMode = (preference, systemDark) =>
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference

const ThemeModeContext = createContext({
    preference: 'system',
    mode: 'light',
    setPreference: () => {},
})

/** The theme preference, the theme it shows, and the setter. */
export const useThemeMode = () => useContext(ThemeModeContext)

/**
 * Applies the light or dark theme to everything under it.
 *
 * The preference (Light, Dark or Match device) is kept in localStorage and
 * defaults to the device setting, which it follows live. The chosen theme is
 * written to `<html data-theme>` before the browser paints (the token CSS
 * variables key off it) and handed to MUI as the matching theme.
 * public/index.html sets the same attribute before React loads, so a dark
 * page never flashes light.
 */
const ThemeModeProvider = ({ children }) => {
    const [preference, setPreferenceState] = useState(readThemePreference)
    const systemDark = useMediaQuery(SYSTEM_DARK_QUERY, { noSsr: true })
    const mode = resolveThemeMode(preference, systemDark)

    useLayoutEffect(() => {
        document.documentElement.setAttribute('data-theme', mode)
    }, [mode])

    const setPreference = useCallback((next) => {
        if (!THEME_PREFERENCES.includes(next)) return
        setPreferenceState(next)
        try {
            if (next === 'system') {
                window.localStorage.removeItem(THEME_STORAGE_KEY)
            } else {
                window.localStorage.setItem(THEME_STORAGE_KEY, next)
            }
        } catch (e) {
            // Storage refused (private mode): the choice lasts this visit.
        }
    }, [])

    const value = useMemo(
        () => ({ preference, mode, setPreference }),
        [preference, mode, setPreference]
    )

    return (
        <ThemeModeContext.Provider value={value}>
            <ThemeProvider theme={mode === 'dark' ? darkTheme : lightTheme}>
                <GlobalStyles styles={themeVariableStyles} />
                {children}
            </ThemeProvider>
        </ThemeModeContext.Provider>
    )
}

ThemeModeProvider.propTypes = { children: PropTypes.node }

export default ThemeModeProvider
